import {
  Avatar, Autocomplete,
  AutocompleteRenderInputParams,
  AutocompleteChangeReason, AutocompleteChangeDetails,
  TextField, Chip,
} from '@mui/material';
import { NetworkAccount } from 'lib/types/networkAccounts';
import { PostingAccountCriteria } from 'lib/types/query';
import {
  SyntheticEvent, useEffect, useMemo, useRef, useState,
} from 'react';
import { useApi } from 'lib/contexts/ApplicationState';
import { isEmpty, throttle } from 'lodash';
import { NETWORKS } from 'lib/constants';
import { getAvatarOrPlaceholder } from 'lib/utils/imageUtils';
import { NetworkIcon } from 'elements/NetworkIcon';

interface Props {
  /** The selected network account(s) */
  value?: NetworkAccount | NetworkAccount[];
  /** Callback when selection changes */
  onChange: (
    event: SyntheticEvent,
    value: NetworkAccount | NetworkAccount[] | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<NetworkAccount>,
  ) => void;
  /** Input label */
  label?: string;
  /** Allow multiple selections */
  multiple?: boolean;
  /** Input size */
  size?: 'medium' | 'small';
  /** Input variant */
  variant?: 'filled' | 'standard' | 'outlined';
  /** Placeholder text */
  placeholder?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Filter results to only show accounts from this specific network ID */
  networkId?: number;
}

export const NetworkAccountFilter = ({
  value,
  onChange,
  label = 'Network Account',
  multiple = false,
  size = 'medium',
  variant = 'outlined',
  placeholder,
  disabled = false,
  networkId,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<NetworkAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const isFetching = useRef(false);
  const API = useApi();

  const fetchAccounts = useMemo(
    () => throttle(async (query: string) => {
      if (isEmpty(query.trim()) || isFetching.current) {
        return;
      }

      isFetching.current = true;
      setLoading(true);

      try {
        const searchParams: PostingAccountCriteria = { account: query.trim() };
        if (networkId) {
          const networkKey = NETWORKS[networkId]?.name;
          if (networkKey) {
            searchParams.networks = networkKey;
          }
        }

        const response = await API.searchNetworkAccounts(searchParams);
        if (response?.data) {
          setOptions(response.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error searching network accounts:', error);
        setOptions([]);
      } finally {
        isFetching.current = false;
        setLoading(false);
      }
    }, 500), // Increased debounce time to 500ms for better pause detection
    [API, networkId],
  );

  useEffect(() => {
    const trimmedInput = inputValue.trim();

    if (trimmedInput.length >= 3) {
      // Only search when we have at least 3 characters
      fetchAccounts(trimmedInput);
    } else {
      // Clear options if input is too short
      setOptions([]);
      // Cancel any pending requests
      isFetching.current = false;
      setLoading(false);
    }
  }, [inputValue, fetchAccounts]);

  const getNetworkName = (netId: number): string => {
    const network = Object.values(NETWORKS).find((n) => n.id === netId);
    return network?.name || 'Unknown';
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      value={value || (multiple ? [] : null)}
      onChange={onChange}
      onInputChange={(_: SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      getOptionLabel={(option: NetworkAccount) => option.account || option.displayName || ''}
      isOptionEqualToValue={(option: NetworkAccount, val: NetworkAccount) => option.id === val.id}
      loading={loading}
      disabled={disabled}
      filterOptions={(optionsList) => optionsList} // Don't filter locally, rely on API
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant={variant}
          fullWidth
          sx={{
            backgroundColor: 'white',
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.5rem',
              backgroundColor: 'white',
            },
          }}
        />
      )}
      renderOption={(props, option: NetworkAccount) => (
        <li {...props} key={option.id}>
          <div className="flex items-center w-full">
            <div className="mr-3">
              <Avatar
                alt={option.displayName || option.account}
                src={getAvatarOrPlaceholder(option.avatarUrl || '', option.id)}
                sx={{ width: 32, height: 32 }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">
                <NetworkIcon network={option.network} style={{ fontSize: '1rem', marginRight: '0.25rem' }} iconColor="color" />
                @
                {option.account}
              </div>
              <div className="text-xs text-gray-500 flex items-center">
                <span className="mr-2">
                  {option.displayName}
                </span>
              </div>
            </div>
            <div className="ml-2 text-right text-xs text-gray-500">
              {option.followersCount?.toLocaleString()}
              {' '}
              followers
            </div>
          </div>
        </li>
      )}
      renderTags={(tagValue: NetworkAccount[], getTagProps) => tagValue.map(
        (option: NetworkAccount, index: number) => (
          <Chip
            label={`${option.displayName || option.account} (${getNetworkName(option.network)})`}
            {...getTagProps({ index })}
            size="small"
            avatar={(
              <Avatar
                src={getAvatarOrPlaceholder(option.avatarUrl || '', option.id)}
                sx={{ width: 20, height: 20 }}
              />
            )}
          />
        ),
      )}
      size={size}
      noOptionsText="No network accounts found"
    />
  );
};
